'use client';
import { useState } from 'react';
import { getSectorColor } from '@/lib/sector-colors';

interface Props {
  sectors: string[];
  selectedSectors: Set<string>;
  onSectorToggle: (s: string) => void;
  selectedIndustries: Set<string>;
  onIndustryToggle: (industry: string) => void;
  onSelectAllSectors: () => void;
  industriesBySector: Record<string, string[]>;
}

export function SectorsPanel({
  sectors,
  selectedSectors,
  onSectorToggle,
  selectedIndustries,
  onIndustryToggle,
  onSelectAllSectors,
  industriesBySector,
}: Props) {
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [industryDrillSector, setIndustryDrillSector] = useState<string | null>(null);
  const allSelected = sectors.every(s => selectedSectors.has(s));

  return (
    <div className="px-5 py-2 border-b border-[#1e293b]">
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => {
            const willOpen = !sectorsOpen;
            setSectorsOpen(willOpen);
            // Collapsing closes any sub-industry drill so re-opening is clean.
            if (!willOpen) setIndustryDrillSector(null);
          }}
          className="flex items-center gap-2 flex-1 cursor-pointer"
        >
          <label className="text-[#e2e8f0] text-[10px] font-semibold uppercase tracking-widest cursor-pointer">
            Sectors {!allSelected && <span className="text-blue-400 ml-1">({selectedSectors.size})</span>}
          </label>
          <svg className={`w-3 h-3 text-white transition-transform duration-150 ${sectorsOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {!allSelected && (
          <button
            onClick={() => { onSelectAllSectors(); setIndustryDrillSector(null); }}
            title="Select every sector + clear any industry filter"
            className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors duration-150 cursor-pointer px-2 py-0.5 rounded border border-blue-500/40 hover:border-blue-500/70 whitespace-nowrap"
          >
            Show all
          </button>
        )}
      </div>
      {sectorsOpen && (
        <div className="mt-3">
          <div className="flex justify-end mb-1">
            <button
              onClick={() => { onSelectAllSectors(); setIndustryDrillSector(null); }}
              disabled={allSelected}
              className={`text-[10px] transition-colors duration-150 ${allSelected ? 'text-[#475569] cursor-default' : 'text-blue-400 hover:text-blue-300 cursor-pointer'}`}
            >
              Select all
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            {sectors.map(sector => {
              const active = selectedSectors.has(sector);
              const industries = industriesBySector[sector] ?? [];
              const hasIndustries = industries.length > 1;
              const showIndustries = active && selectedSectors.size === 1 && hasIndustries && industryDrillSector === sector;
              const noIndustriesPicked = selectedIndustries.size === 0;
              return (
                <div key={sector}>
                  <button
                    onClick={() => {
                      if (active && selectedSectors.size === 1 && hasIndustries) {
                        setIndustryDrillSector(industryDrillSector === sector ? null : sector);
                        return;
                      }
                      onSectorToggle(sector);
                      if (hasIndustries) setIndustryDrillSector(sector);
                    }}
                    className={`flex items-center gap-2.5 text-left text-[11px] px-2.5 py-1.5 rounded-md w-full
                               transition-all duration-150 cursor-pointer
                               ${active
                                 ? 'text-white bg-white/[0.04] hover:bg-white/[0.07]'
                                 : 'text-white hover:text-[#e2e8f0] hover:bg-white/[0.02]'}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 transition-all duration-150"
                      style={{
                        backgroundColor: active ? getSectorColor(sector) : '#334155',
                        boxShadow: active ? `0 0 6px ${getSectorColor(sector)}40` : 'none',
                      }}
                    />
                    <span className="flex-1">{sector}</span>
                    {showIndustries && !noIndustriesPicked && (
                      <span className="text-[9px] text-blue-400 tabular-nums">
                        {selectedIndustries.size}
                      </span>
                    )}
                  </button>
                  {showIndustries && (
                    <div className="ml-5 mt-0.5 mb-1 flex flex-col gap-0.5 border-l border-[#1e293b] pl-2">
                      <div className="flex items-baseline justify-between text-[9px] text-[#64748b] uppercase tracking-wide pr-2 pt-1 pb-0.5">
                        <span>industries ({industries.length})</span>
                        {!noIndustriesPicked && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              for (const ind of industries) {
                                if (selectedIndustries.has(ind)) onIndustryToggle(ind);
                              }
                            }}
                            className="text-blue-400 hover:text-blue-300 normal-case tracking-normal"
                          >
                            clear
                          </button>
                        )}
                      </div>
                      {industries.map(ind => {
                        const indActive = selectedIndustries.has(ind);
                        const showAsActive = noIndustriesPicked || indActive;
                        return (
                          <button
                            key={ind}
                            onClick={() => onIndustryToggle(ind)}
                            className={`flex items-center gap-2 text-left text-[10.5px] px-2 py-0.5 rounded
                                       transition-colors cursor-pointer
                                       ${showAsActive
                                         ? 'text-[#e2e8f0] hover:bg-white/[0.04]'
                                         : 'text-[#64748b] hover:text-[#94a3b8] hover:bg-white/[0.02]'}`}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: showAsActive ? getSectorColor(sector) : '#334155',
                                opacity: showAsActive ? (indActive ? 1 : 0.5) : 0.3,
                              }}
                            />
                            {ind}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
